'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Video } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CHARACTER_THEMES } from '@/lib/character-themes'
import {
  isOneShot,
  SANTA_CALL_LOOP,
  SANTA_GESTURE_CLIPS,
  SANTA_IDLE_LOOP,
  SANTA_IDLE_POSTER,
  SANTA_ONE_SHOT_GESTURES,
  SANTA_TALK_LOOP,
  type SantaOneShotGesture,
} from '@/lib/santa-chat/gestures.manifest'
import type { SantaGesture } from '@/server/domain/santa-chat'

interface SantaVideoPanelProps {
  gesture: SantaGesture
  /** Cambia con cada gesto: reinicia el clip aunque se repita el mismo gesto. */
  gestureTurn: number
  /** Su voz está sonando (no la risa): habla en bucle. */
  speaking: boolean
  /** El chat ofrece llamar: pide que le llamen con un botón grande encima. */
  call: { label: string; onCall: () => void } | null
  /** Cuando el navegador bloqueó la voz: texto del botón grande para escucharla. */
  unblockLabel?: string
  onUnblock?: () => void
}

const theme = CHARACTER_THEMES.santa.videoCall
/**
 * Retrato vertical (póster y clips): llena el panel y recorta por los lados o por abajo,
 * nunca la cabeza. En la franja de móvil sube un 20 % para no gastar altura en la pared
 * de encima del gorro; en la columna de escritorio cabe entero desde arriba (`object-top`).
 */
const FRAME_FIT = 'object-cover object-[50%_20%] lg:object-top'
const STAGE_VIDEO = cn('absolute inset-0 h-full w-full', FRAME_FIT)
/** Lo que dura el fundido entre capas (`duration-200`); después, la que se va se pausa. */
const FADE_MS = 200
type SceneName = SantaOneShotGesture | 'talk' | 'call'

/** Las escenas que pueden verse sobre el reposo: los bucles y, después, los clips de una vez. */
const SCENES: { name: SceneName; src: string; loop: boolean }[] = [
  { name: 'talk', src: SANTA_TALK_LOOP, loop: true },
  { name: 'call', src: SANTA_CALL_LOOP, loop: true },
  ...SANTA_ONE_SHOT_GESTURES.map((name) => ({ name, src: SANTA_GESTURE_CLIPS[name].videoSrc, loop: false })),
]

/** Qué escena se ve encima del reposo: un clip propio manda; si no, habla mientras suena la voz o pide la llamada. */
function visibleScene(gesture: SantaGesture, oneShotDone: boolean, speaking: boolean, calling: boolean): SceneName | null {
  if (isOneShot(gesture) && !oneShotDone) return gesture
  if (speaking) return 'talk'
  return calling ? 'call' : null
}

interface VideoLayerProps {
  name: string
  src: string
  visible: boolean
  /** Vuelve a empezar desde el principio cada vez que cambia mientras se ve (el turno, en los clips de una vez). */
  restartKey?: number
  loop?: boolean
  preload: 'auto' | 'metadata'
  onEnded?: () => void
}

/** Una escena sobre el reposo: entra con un fundido desde su primer fotograma y, al irse, se pausa. */
function VideoLayer({ name, src, visible, restartKey = 0, loop = false, preload, onEnded }: VideoLayerProps) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const video = ref.current
    if (!video) return
    if (visible) {
      video.currentTime = 0
      // `play()` rechaza si el navegador lo impide; el póster y el reposo ya cubren ese caso.
      video.play()?.catch(() => {})
      return
    }
    const timer = setTimeout(() => video.pause(), FADE_MS)
    return () => clearTimeout(timer)
  }, [visible, restartKey])
  return (
    <video
      ref={ref}
      data-layer={name}
      data-visible={visible}
      src={src}
      loop={loop}
      muted
      playsInline
      preload={preload}
      aria-hidden
      // Only the scene on screen may close its turn: one fading out could otherwise end the next one.
      onEnded={visible ? onEnded : undefined}
      // `visibility` flips at the end of the fade, so hidden layers drop their composited layer.
      className={cn(
        STAGE_VIDEO,
        'transition-[opacity,visibility] duration-200 motion-reduce:transition-none',
        visible ? 'visible opacity-100' : 'invisible opacity-0'
      )}
    />
  )
}

/**
 * El reposo en bucle, siempre debajo. Mientras otra escena se ve se queda parado en su primer
 * fotograma, que es el póster: los clips empiezan y acaban ahí, así que las uniones no saltan
 * y el móvil solo decodifica un vídeo.
 */
function IdleLayer({ running, onLoaded }: { running: boolean; onLoaded: () => void }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const video = ref.current
    if (!video) return
    if (running) {
      video.play()?.catch(() => {})
      return
    }
    video.pause()
    video.currentTime = 0
  }, [running])
  return (
    <video
      ref={ref}
      data-layer="idle"
      loop
      muted
      playsInline
      aria-hidden
      onCanPlayThrough={onLoaded}
      className={cn(STAGE_VIDEO, 'motion-reduce:hidden')}
    >
      {/* With reduced motion the browser never fetches it: the still poster stays. */}
      <source src={SANTA_IDLE_LOOP} type="video/mp4" media="(prefers-reduced-motion: no-preference)" />
    </video>
  )
}

function CallButton({ label, onCall }: { label: string; onCall: () => void }) {
  return (
    <button
      type="button"
      onClick={onCall}
      className={cn(
        'absolute inset-x-4 bottom-3 z-10 flex min-h-12 items-center justify-center gap-3 rounded-2xl px-4 py-2 text-base font-semibold lg:bottom-6 lg:min-h-16 lg:text-xl',
        // The `pulse-glow` halo with a fixed shadow and only its opacity animated: it repaints nothing over the video.
        'before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-2xl before:shadow-[0_0_40px_rgba(255,215,0,0.6)] before:animate-pulse',
        theme.ctaButton
      )}
    >
      <Video aria-hidden className="size-7 shrink-0" />
      <span>{label}</span>
    </button>
  )
}

/**
 * Panel fijo con Papá Noel: arriba en móvil, columna izquierda a toda altura desde `lg`.
 * Debajo, el reposo en bucle (el póster quieto con movimiento reducido). Encima, una escena a
 * la vez: el clip propio del gesto (una vez), hablar en bucle mientras suena su voz o, cuando
 * el chat ofrece llamar y está callado, «llámame» en bucle con el botón de videollamada.
 */
export function SantaVideoPanel({ gesture, gestureTurn, speaking, call, unblockLabel, onUnblock }: SantaVideoPanelProps) {
  const [endedTurn, setEndedTurn] = useState<number | null>(null)
  // Metadata until the idle loop has loaded (full clips would compete with the poster, POST /open
  // and the greeting on mobile networks); then whole, so the first scene plays from cache.
  const [warm, setWarm] = useState(false)
  const preload = warm ? 'auto' : 'metadata'
  const shown = visibleScene(gesture, endedTurn === gestureTurn, speaking, call !== null)

  return (
    <div
      data-testid="santa-video-panel"
      data-gesture={gesture}
      className={cn(
        'relative h-[38dvh] max-h-96 min-h-48 w-full shrink-0 overflow-hidden lg:h-full lg:max-h-none lg:w-2/5 lg:max-w-xl',
        theme.stageGradient
      )}
    >
      {/* LCP of the screen: Next 16 deprecates `priority`; eager + high fetch priority, as in hero-collage. */}
      <Image src={SANTA_IDLE_POSTER} alt="" fill loading="eager" fetchPriority="high" sizes="(min-width: 1024px) 40vw, 100vw" className={FRAME_FIT} />
      <IdleLayer running={shown === null} onLoaded={() => setWarm(true)} />
      {SCENES.map(({ name, src, loop }) => (
        <VideoLayer
          key={name}
          name={name}
          src={src}
          loop={loop}
          visible={shown === name}
          restartKey={loop ? undefined : gestureTurn}
          preload={preload}
          onEnded={loop ? undefined : () => setEndedTurn(gestureTurn)}
        />
      ))}
      {shown === 'call' && call && <CallButton label={call.label} onCall={call.onCall} />}
      {unblockLabel && onUnblock && (
        <button
          type="button"
          onClick={onUnblock}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/45 text-2xl font-semibold text-white backdrop-blur-[2px]"
        >
          {`▶ ${unblockLabel}`}
        </button>
      )}
    </div>
  )
}
